import { FormControl, Text } from '@chakra-ui/react';
import type { UTCDate } from '@date-fns/utc';
import classNames from 'classnames';
import { clamp } from 'date-fns';
import mixpanel from 'mixpanel-browser';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import type { SingleValue } from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';

import { AiSearchTrackingEvents } from '@revelio/core';
import { Select } from '@revelio/ui';

import { SelectionListControls } from '../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import { useTabContentConfig } from '../../../components/PageConfigProvider/PageConfigProvider';
import {
  type DateRangeOption,
  DateRangePresets,
} from '../../constants/datePresets';
import { useAvailableDateRangeWithTrialLag } from '../../hooks/useAvailableDateRangeWithTrialLag';
import { useDateRangeFilter, useFilterState } from '../../hooks/useFilters';
import { useTrackFilterReset } from '../../hooks/useTrackFilterReset';
import { useViewDateGranularity } from '../../hooks/useViewDateGranularity';
import {
  type DateRangeFilter,
  FilterId,
  FilterType,
} from '../../types/filters';
import { localToUTC, utcToLocal } from '../../utils/dateUtils';
import overlaySelectStyles from '../filter-overlay-select.module.css';
import './DateRangeFilter.css';

import styles from './date-range-filter-form.module.css';
import {
  computeDateRangeFromOption,
  identifyDateRangePreset,
  validateDateRange,
} from './utils';

interface DateRangeFilterFormProps {
  closeMenu: () => void;
  allowedPresetValues?: string[];
  /** Which date-range filter this form drives. Defaults to DATE_RANGE. */
  filterId?: FilterId;
  /**
   * Force the picker granularity instead of deriving it from the page's
   * metrics. Used by the posting Post Date filter to stay day-level ('week').
   */
  granularity?: 'week' | 'month';
}

export const DateRangeFilterForm: React.FC<DateRangeFilterFormProps> = ({
  closeMenu,
  allowedPresetValues,
  filterId = FilterId.DATE_RANGE,
  granularity,
}) => {
  const { setDateRange } = useDateRangeFilter(filterId);
  const { revelioPageId } = useTabContentConfig();
  const { availableDateRange } = useAvailableDateRangeWithTrialLag();
  const { filterState: rawDateRangeFilter } =
    useFilterState<DateRangeFilter>(filterId);
  const pageGranularity = useViewDateGranularity();
  const viewGranularity = granularity ?? pageGranularity;
  const trackFilterReset = useTrackFilterReset();

  const [selectedOption, setSelectedOption] = useState<DateRangeOption | null>(
    null
  );

  const [localStartDate, setLocalStartDate] = useState<UTCDate | null>(null);
  const [localEndDate, setLocalEndDate] = useState<UTCDate | null>(null);

  const [error, setError] = useState<string>('');

  const systemMinDate =
    viewGranularity === 'month'
      ? (availableDateRange?.month.min ?? null)
      : (availableDateRange?.week.min ?? null);
  const systemMaxDate =
    viewGranularity === 'month'
      ? (availableDateRange?.month.max ?? null)
      : (availableDateRange?.week.max ?? null);

  const isLoading = !availableDateRange;

  useEffect(() => {
    if (isLoading || !systemMinDate || !systemMaxDate) return;

    const displayStartDate = rawDateRangeFilter?.min
      ? (clamp(rawDateRangeFilter.min, {
          start: systemMinDate,
          end: systemMaxDate,
        }) as UTCDate)
      : null;
    const displayEndDate = rawDateRangeFilter?.max
      ? (clamp(rawDateRangeFilter.max, {
          start: systemMinDate,
          end: systemMaxDate,
        }) as UTCDate)
      : null;

    const presetKey = identifyDateRangePreset(
      rawDateRangeFilter,
      viewGranularity,
      availableDateRange
    );

    const foundOption =
      DateRangePresets.find((o: DateRangeOption) => o.value === presetKey) ||
      null;

    setSelectedOption(foundOption);
    setLocalStartDate(displayStartDate);
    setLocalEndDate(displayEndDate);
    setError('');
  }, [
    rawDateRangeFilter,
    isLoading,
    systemMinDate,
    systemMaxDate,
    viewGranularity,
    availableDateRange,
  ]);

  const updateLocalDates = useCallback(
    (option: DateRangeOption) => {
      setSelectedOption(option);
      setError('');

      if (isLoading || !systemMinDate || !systemMaxDate) {
        setLocalStartDate(null);
        setLocalEndDate(null);
        return;
      }

      if (option.value === 'max') {
        setLocalStartDate(null);
        setLocalEndDate(null);
      } else if (option.value === 'custom') {
        setLocalStartDate(rawDateRangeFilter?.min ?? localStartDate);
        setLocalEndDate(rawDateRangeFilter?.max ?? localEndDate);
      } else {
        const computed = computeDateRangeFromOption(
          option,
          availableDateRange,
          viewGranularity
        );
        if (computed) {
          let finalStartDate: UTCDate | null =
            computed.start < availableDateRange[viewGranularity].min
              ? availableDateRange[viewGranularity].min
              : computed.start;
          let finalEndDate: UTCDate | null =
            computed.end > availableDateRange[viewGranularity].max
              ? availableDateRange[viewGranularity].max
              : computed.end;

          if (finalStartDate && finalEndDate && finalStartDate > finalEndDate) {
            finalStartDate = availableDateRange[viewGranularity].min;
            finalEndDate = availableDateRange[viewGranularity].max;
          }
          setLocalStartDate(finalStartDate);
          setLocalEndDate(finalEndDate);
        } else {
          setLocalStartDate(availableDateRange[viewGranularity].min);
          setLocalEndDate(availableDateRange[viewGranularity].max);
        }
      }
    },
    [
      isLoading,
      systemMinDate,
      systemMaxDate,
      rawDateRangeFilter?.min,
      rawDateRangeFilter?.max,
      localStartDate,
      localEndDate,
      availableDateRange,
      viewGranularity,
    ]
  );

  const handleOptionChange = useCallback(
    (newOption: SingleValue<DateRangeOption>) => {
      if (!newOption) return;
      updateLocalDates(newOption);
    },
    [updateLocalDates]
  );

  const handleApply = () => {
    if (isLoading || (selectedOption?.value === 'custom' && error)) return;
    if (!selectedOption) return;

    // Save the raw local dates representing user intent
    setDateRange(
      localStartDate ? localToUTC(localStartDate) : null,
      localEndDate ? localToUTC(localEndDate) : null
    );
    // Same shape as the other manual filter forms; the preset (not the date
    // values) keeps this low-cardinality.
    mixpanel.track(AiSearchTrackingEvents.FILTERS_APPLIED, {
      method: 'manual',
      filter_id: filterId,
      filter_type: FilterType.DATE_RANGE,
      date_preset: selectedOption.value,
      page_id: revelioPageId,
    });
    closeMenu();
  };

  const handleClear = () => {
    trackFilterReset(
      { id: filterId, type: FilterType.DATE_RANGE },
      { selectionCountBefore: localStartDate || localEndDate ? 1 : 0 }
    );
    setError('');
    setDateRange(null, null);
    closeMenu();
  };

  useEffect(() => {
    if (selectedOption?.value !== 'custom') return;
    const msg = validateDateRange(
      localStartDate,
      localEndDate,
      systemMinDate,
      systemMaxDate
    );
    setError(msg);
  }, [
    localStartDate,
    localEndDate,
    selectedOption,
    systemMinDate,
    systemMaxDate,
  ]);

  const isCustomSelected = selectedOption?.value === 'custom';

  const commonPickerProps = (() => {
    const base = {
      minDate: systemMinDate ?? undefined,
      maxDate: systemMaxDate ?? undefined,
      placeholderText: viewGranularity === 'week' ? 'YYYY-MM-DD' : 'YYYY-MM',
    };

    if (viewGranularity === 'month') {
      return {
        ...base,
        showMonthYearPicker: true,
        dateFormat: 'yyyy-MM',
        showMonthDropdown: false,
        showYearDropdown: true,
        dropdownMode: 'select',
        yearDropdownItemNumber: 15,
        scrollableYearDropdown: true,
      } as const;
    } else {
      return {
        ...base,
        showWeekPicker: true,
        showWeekNumbers: true,
        dateFormat: 'yyyy-MM-dd',
        showMonthDropdown: true,
        showYearDropdown: true,
        dropdownMode: 'select',
        yearDropdownItemNumber: 15,
        scrollableYearDropdown: true,
      } as const;
    }
  })();

  const renderDateInput = (
    label: string,
    selected: UTCDate | null,
    onChange: (date: UTCDate | null) => void,
    pickerMinDate: UTCDate | null,
    pickerMaxDate: UTCDate | null,
    isInvalid?: boolean,
    testId?: string
  ) => (
    <FormControl flex={1} isDisabled={isLoading}>
      <Text fontSize="sm" pb={1}>
        {label}
      </Text>
      <DatePicker
        selected={selected ? utcToLocal(selected) : null}
        onChange={(date) => onChange(date ? localToUTC(date) : null)}
        {...commonPickerProps}
        minDate={pickerMinDate ? utcToLocal(pickerMinDate) : null}
        maxDate={pickerMaxDate ? utcToLocal(pickerMaxDate) : null}
        onBlur={(event) => {
          event.stopPropagation();
        }}
        customInput={
          <input
            className={styles.dateInput}
            data-invalid={isInvalid || undefined}
            data-testid={testId}
            autoComplete="off"
          />
        }
      />
    </FormControl>
  );

  return (
    <div className={styles.form}>
      <FormControl>
        <Select<DateRangeOption, false>
          options={DateRangePresets.filter(
            (option) =>
              option.hidden !== true &&
              (!allowedPresetValues ||
                allowedPresetValues.includes(option.value))
          )}
          value={selectedOption}
          onChange={handleOptionChange}
          size="sm"
          isDisabled={isLoading}
          id="filter-range"
          isSearchable={false}
          className={classNames(
            overlaySelectStyles.overlaySelect,
            overlaySelectStyles.operatorSelect
          )}
          classNamePrefix="filter-overlay-select"
        />
      </FormControl>

      {isCustomSelected && (
        <div className={styles.customRange}>
          <div>
            <div className={styles.dateInputs}>
              {renderDateInput(
                'Start Date',
                localStartDate,
                setLocalStartDate,
                systemMinDate ?? null,
                localEndDate || systemMaxDate || null,
                !!(
                  error &&
                  localStartDate &&
                  systemMinDate &&
                  localStartDate < systemMinDate
                ),
                'custom-range-start-date'
              )}
              {renderDateInput(
                'End Date',
                localEndDate,
                setLocalEndDate,
                localStartDate || systemMinDate || null,
                systemMaxDate ?? null,
                !!(
                  error &&
                  localEndDate &&
                  systemMaxDate &&
                  localEndDate > systemMaxDate
                ),
                'custom-range-end-date'
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <Text fontSize="sm" color="red.500">
          {error}
        </Text>
      )}

      <SelectionListControls
        onClear={handleClear}
        onSubmit={handleApply}
        onClose={closeMenu}
        clearText="Clear"
        submitText="Apply"
        isSubmitDisabled={!!error || isLoading}
      />
    </div>
  );
};
